import React, { ChangeEvent, JSX, useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Dropdown, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Dynamics } from '@/services/dynamics/dynamics';
import { Sorting } from '@/components/Sorting/Sorting';
import sortIcon from '@/public/icons/sort.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { BusEvent, EBusEvent } from '@/services/bus-event/bus-event.service';
import { IChatEntity } from '@/shared/interfaces/chat-entity.interface';
import { ChatEntity } from '@/components/ChatEntity/ChatEntity';
import { IPhoneCall } from '@/shared/interfaces/phone-call.interface';
import { IHistoryItem } from '@/shared/interfaces/history-item.interface';
import { HistoryIcon } from '@/components/HistoryIcon/HistoryIcon';
import { DirectionHistoryType } from '@/shared/interfaces/history-filter.interface';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/stores';
import InputSearch from '@/components/InputSearch/InputSearch';
import { FilterIcon } from '@/public/icons/filters/FilterIcon';
import { State } from '@/services/state.service';
import { logger } from '@/services/logger.service';
import './ActivityHistory.scss';

const antIcon = <LoadingOutlined spin />;
const { RangePicker } = DatePicker;
const { Title } = Typography;

export const ActivityHistory = observer(() => {
  const {
    chatsStore: {
      getHistoryItemsFromApi,
      history,
      historyFilter,
      setHistoryFilter,
    },
  } = useStore();
  const { searchTerm, orderBy, dateRange } = historyFilter;

  const prevElItem = useRef<HTMLDivElement>(null);
  const datePicker = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getEntityComponent = (
    item: IHistoryItem,
    isPrevious: boolean,
  ): JSX.Element => {
    if (item.type === 'CHAT') {
      return (
        <ChatEntity
          isPrevious={isPrevious}
          chatEntity={item.item as IChatEntity}
        ></ChatEntity>
      );
    }

    const phoneCall = item.item as IPhoneCall;

    return (
      <HistoryIcon
        phoneNumber={phoneCall.phoneNumber}
        key={phoneCall.id}
        fullName={phoneCall.fullName}
        date={phoneCall.date}
        direction={phoneCall.direction}
        subject={phoneCall.subject}
        isPrevious={isPrevious}
        isExternal={!phoneCall.durationFormatted}
      />
    );
  };

  const openChatDetails = async (item: IChatEntity) => {
    logger.log(`[ActivityHistory] Open chat details: ${item.chatId}`);
    navigate(`/activity-history/${item.chatId}`, {
      state: { from: '/activity-history' },
    });
  };

  const openCallEntity = async (entity: IPhoneCall) => {
    if (entity.direction === 'noanswer') {
      navigate('/call-details', { state: { entity } });
    } else {
      const res = await Dynamics.openPhoneCall(entity.id);
      const nextState = { id: res.activityid };
      navigate('/call-record', {
        state: nextState,
      });
    }
  };

  const getPhoneCallActivities = async () => {
    setIsLoaded(false);
    await getHistoryItemsFromApi();
    setIsLoaded(true);
  };

  useEffect(() => {
    const { prevHistoryChatId } = State.state$.getValue();

    if (isLoaded && prevHistoryChatId && prevElItem?.current) {
      prevElItem.current.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
  }, [location.state, isLoaded]);

  useEffect(() => {
    getPhoneCallActivities();

    const sub = BusEvent.getBus$().subscribe((data: { event: EBusEvent }) => {
      if (data.event === EBusEvent.UPD_HISTORY) {
        getPhoneCallActivities();
      }
    });

    return () => sub.unsubscribe();
  }, []);

  const onSortingApplied = (value: DirectionHistoryType) => {
    setHistoryFilter({
      orderBy: {
        field: 'createAt',
        direction: value,
      },
    });
  };

  const handleClick = (item: IHistoryItem) => {
    State.setState({ prevHistoryChatId: item.id });

    if (item.type === 'CHAT') {
      openChatDetails(item.item as IChatEntity);
      return;
    }

    openCallEntity(item.item as IPhoneCall);
  };

  const onDateSortChange = (values) => {
    if (!values || !values[0] || !values[1]) {
      setHistoryFilter({
        dateRange: {
          fromDate: null,
          toDate: null,
        },
      });
      return;
    }

    const newFromDate = values[0]
      .set({ hour: 0o0, minute: 0o0, second: 0o1 })
      .toDate();
    const newToDate = values[1]
      .set({ hour: 23, minute: 59, second: 59 })
      .toDate();

    setHistoryFilter({
      dateRange: {
        fromDate: newFromDate,
        toDate: newToDate,
      },
    });
  };

  const closeDatePickerBtn = () => {
    return (
      <Button
        data-testid="close-date-picker"
        onClick={() => datePicker.current.blur()}
        size="small"
        type="primary"
        ghost
      >
        Close
      </Button>
    );
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.target.value;
    setHistoryFilter({ searchTerm: value });
  };

  return (
    <div className="activity-history-page inbox-page">
      <div className="heading">
        <Title level={5}>
          History {history?.length > 0 && isLoaded ? `(${history.length})` : ''}
        </Title>

        <span>
          <Dropdown
            overlay={() => (
              <Sorting
                setSortingApplied={onSortingApplied}
                sortingApplied={orderBy.direction}
              />
            )}
            placement="bottom"
            arrow
          >
            <button className="heading-button">
              <img src={sortIcon} alt="sort" />
            </button>
          </Dropdown>
        </span>
      </div>
      <div>
        <button className="heading-button">
          <RangePicker
            ref={datePicker}
            suffixIcon={<FilterIcon />}
            bordered={false}
            onChange={onDateSortChange}
            renderExtraFooter={closeDatePickerBtn}
            value={
              dateRange.fromDate && dateRange.toDate
                ? [moment(dateRange.fromDate), moment(dateRange.toDate)]
                : undefined
            }
          />
        </button>
      </div>
      <div>
        <div className="history-search-container">
          <InputSearch
            onChange={onChange}
            data-testid="history-search-input"
            placeholder="Search by client"
            value={searchTerm}
          />
        </div>
      </div>
      {isLoaded ? (
        <div className="history-content">
          {history.map((item, i) => {
            const { prevHistoryChatId } = State.state$.getValue();
            const isPrevious = item.id === prevHistoryChatId;
            const entity = getEntityComponent(item, isPrevious);

            return (
              <div
                ref={isPrevious ? prevElItem : null}
                className={'user-inbox-item'}
                onClick={() => handleClick(item)}
                key={`i${i}${item.id}`}
              >
                {entity}
              </div>
            );
          })}
        </div>
      ) : (
        <Spin className="spinner" indicator={antIcon} />
      )}
    </div>
  );
});
